import type { CallbackEvent } from "@shopify/polaris-types";
import type { ButtonStyle } from "@codway-dev/ui";
import {
  CollapsibleSectionCard,
  SectionHeader,
  Divider,
  ColorRow,
  NumericField,
} from "../../../components/shared/EditorShared";

export const DEFAULT_ACCEPT_STYLE: ButtonStyle = {
  text: "Agregar a tu pedido",
  backgroundColor: "#000000",
  textColor: "#ffffff",
  borderColor: "#000000",
  borderWidth: 0,
  borderRadius: 8,
  textSize: 14,
  shadow: 0,
  animation: "none",
};

export const DEFAULT_DECLINE_STYLE: ButtonStyle = {
  text: "No gracias, completar mi pedido",
  backgroundColor: "#ffffff",
  textColor: "#000000",
  borderColor: "#000000",
  borderWidth: 0,
  borderRadius: 8,
  textSize: 14,
  shadow: 0,
  animation: "none",
};

// ─── Shared button style fields ───────────────────────────────────────────────

const ANIMATION_OPTIONS: { value: ButtonStyle["animation"]; label: string }[] =
  [
    { value: "none", label: "Ninguna" },
    { value: "pulse", label: "Pulso" },
    { value: "shake", label: "Vibración" },
    { value: "bounce", label: "Rebote" },
  ];

function ButtonStyleFields({
  style,
  onChange,
}: {
  style: ButtonStyle;
  onChange: (updated: Partial<ButtonStyle>) => void;
}) {
  return (
    <s-stack direction="block" gap="base">
      <s-text-field
        label="Texto del botón"
        value={style.text}
        onInput={(e: CallbackEvent<"s-text-field">) =>
          onChange({ text: e.currentTarget.value })
        }
      />

      <Divider />

      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <ColorRow
          label="Color de fondo"
          value={style.backgroundColor}
          onChange={(v) => onChange({ backgroundColor: v })}
        />
        <ColorRow
          label="Color del texto"
          value={style.textColor}
          onChange={(v) => onChange({ textColor: v })}
        />
      </s-grid>

      <Divider />

      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <ColorRow
          label="Color del borde"
          value={style.borderColor}
          onChange={(v) => onChange({ borderColor: v })}
        />
        <NumericField
          label="Ancho del borde (px)"
          min={0}
          value={style.borderWidth}
          onChange={(v) => onChange({ borderWidth: v })}
        />
      </s-grid>

      <Divider />

      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <NumericField
          label="Radio del borde (px)"
          min={0}
          value={style.borderRadius}
          onChange={(v) => onChange({ borderRadius: v })}
        />
        <NumericField
          label="Tamaño del texto (px)"
          min={10}
          value={style.textSize}
          onChange={(v) => onChange({ textSize: v })}
        />
      </s-grid>

      <Divider />

      <s-grid gridTemplateColumns="1fr 1fr" gap="base">
        <NumericField
          label="Sombra"
          min={0}
          value={style.shadow}
          onChange={(v) => onChange({ shadow: v })}
        />
        <s-select
          label="Animación"
          value={style.animation}
          onChange={(e: CallbackEvent<"s-select">) =>
            onChange({
              animation: e.currentTarget.value as ButtonStyle["animation"],
            })
          }
        >
          {ANIMATION_OPTIONS.map((opt) => (
            <s-option key={opt.value} value={opt.value}>
              {opt.label}
            </s-option>
          ))}
        </s-select>
      </s-grid>
    </s-stack>
  );
}

// ─── Section 8: Botón Aceptar ─────────────────────────────────────────────────

export function AcceptButtonSection({
  style,
  onChange,
}: {
  style: ButtonStyle;
  onChange: (updated: Partial<ButtonStyle>) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={8} title='Botón "Agregar al pedido"' />}
    >
      <s-stack direction="block" gap="base">
        <ButtonStyleFields style={style} onChange={onChange} />
      </s-stack>
    </CollapsibleSectionCard>
  );
}

// ─── Section 9: Botón Rechazo ─────────────────────────────────────────────────

export function DeclineButtonSection({
  style,
  onChange,
}: {
  style: ButtonStyle;
  onChange: (updated: Partial<ButtonStyle>) => void;
}) {
  return (
    <CollapsibleSectionCard
      header={<SectionHeader number={9} title='"No gracias" botón' />}
    >
      <s-stack direction="block" gap="base">
        <ButtonStyleFields style={style} onChange={onChange} />
      </s-stack>
    </CollapsibleSectionCard>
  );
}
